import React, { useState } from 'react'
import { useClientPreferences } from '../../../context/useClientPreferences'
import { useWorkspace } from '../../../context/useWorkspace'
import { useSharedDeviceClaim } from '../../../services/opsSharedDevicesService'
import { useNls } from '../../../nls/nlsProvider'
import {
  Slider,
  Button,
  SettingsSection,
  SettingsRow,
  SettingsSaveBar,
  useSettingsDraft,
} from '../../Common'
import { HelperSharedDevicePinModal } from '../../Helper/HelperSharedDevicePinModal'

export interface SecurityPanelProps {
  workspaceId?: string
}

export const SecurityPanel: React.FC<SecurityPanelProps> = ({
  workspaceId: propWorkspaceId,
}) => {
  const { preferences, updatePreferences } = useClientPreferences()
  const { activeWorkspaceId } = useWorkspace()
  const workspaceId = propWorkspaceId || activeWorkspaceId || ''
  const isSharedDevice = useSharedDeviceClaim()
  const { t } = useNls()
  const [pinModalOpen, setPinModalOpen] = useState(false)

  const {
    draft: timeoutMinutes,
    set: setTimeoutMinutes,
    dirty,
    saving,
    reset,
    commit,
  } = useSettingsDraft<number>({
    initialValue: preferences.watchdogLeaseTimeoutMinutes,
    onSave: async (draft) => {
      await updatePreferences({ watchdogLeaseTimeoutMinutes: draft })
    },
  })

  return (
    <div className="skSectionStack">
      <SettingsSection title={t('NLSSettingsSectionSession')}>
        <SettingsRow
          label={t('NLSSettingsLabelWatchdogLease')}
          hint={t('NLSSettingsHintWatchdogLease')}
          control={
            <div className="settingsSliderRow">
              <Slider
                min={1}
                max={60}
                value={timeoutMinutes}
                onChange={(next) => setTimeoutMinutes(next)}
                aria-label={t('NLSSettingsAriaWatchdogLeaseTimeout')}
                wrapperClassName="settingsCommonSlider"
              />
              <strong>{timeoutMinutes}m</strong>
            </div>
          }
        />
        <SettingsRow
          label={t('NLSSettingsLabelCacheHandling')}
          hint={t('NLSSettingsBodyCacheHandling')}
        />
        {!isSharedDevice && workspaceId ? (
          <SettingsRow
            label={t('NLSOpsSharedDeviceSetPinTitle')}
            hint={t('NLSOpsSharedDeviceSetPinDesc')}
            testId="shared-device-pin-row"
            control={
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPinModalOpen(true)}
                data-testid="open-helper-pin-modal-btn"
              >
                {t('NLSOpsSharedDeviceSetPinTitle')}
              </Button>
            }
          />
        ) : null}
      </SettingsSection>

      <SettingsSaveBar
        dirty={dirty}
        saving={saving}
        onSave={() => void commit()}
        onDiscard={reset}
      />

      {pinModalOpen && workspaceId ? (
        <HelperSharedDevicePinModal
          open={pinModalOpen}
          onClose={() => setPinModalOpen(false)}
          workspaceId={workspaceId}
        />
      ) : null}
    </div>
  )
}
